import React, { FC, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import CustomTextField from '../../../common/components/CustomTextField';
import { isValidInput } from '../../../common/validator';
import CustomPhoneInput from '../../../common/components/CustomPhoneInput';
import { useCreateBusiness } from '../../../hooks/mutations/useCreateBusiness';
import { CreateBusinessInput } from '../../../types/graphql-global-types';
import { useGetApas } from '../../../hooks/queries/useGetApas';
import { formatName } from '../../../utils/common';
import CustomAutoComplete from '../../../common/components/CustomAutoComplete';
import { useGetMandataries } from '../../../hooks/queries/useGetMandataries';
import { displaySnackBar } from '../../../common/displaySnackbar';
import { useApolloClient } from '@apollo/react-hooks';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';

interface ICreateBusinessForms {
  onCancel: () => void;
  onCreate: () => void;
}

const initialBusiness: CreateBusinessInput = {
  email: '',
  firstName: '',
  access:
    'https://deep-link.easychain.fr?utm_campaign=banner&apn=com.easychain&ibi=com.connectcredit.app&link=https%3A%2F%2Fdeep-link.easychain.fr%2Fid%2F',
};

interface IInput {
  type: 'text' | 'select' | 'tel' | 'email' | 'number' | 'autoComplete';
  label: string;
  value: unknown;
  options?: { label: string; value: unknown }[];
  loading?: boolean;
  key: keyof CreateBusinessInput;
  required?: boolean;
  helperText?: string;
}

const CreateBusinessForms: FC<ICreateBusinessForms> = (props) => {
  const { onCancel, onCreate } = props;

  const client = useApolloClient();

  const [business, setBusiness] = useState<CreateBusinessInput>(
    initialBusiness,
  );
  const [isFinishFillInputs, setIsFinishFillInputs] = useState<boolean>(false);

  const [
    createBusiness,
    { loading: createBusinessLoading },
  ] = useCreateBusiness();

  const { data: apas, loading: apasLoading } = useGetApas();

  const {
    data: mandataries,
    loading: mandatariesLoading,
  } = useGetMandataries();

  const apaOptions = useMemo(
    () =>
      apas?.apas?.map((value) => ({
        label: formatName({
          firstName: value?.firstName ?? '',
          lastName: value?.lastName ?? '',
        }),
        value,
      })),
    [apas],
  );

  const mandataryOptions = useMemo(
    () =>
      mandataries?.mandataries?.map((value) => ({
        label: formatName({
          firstName: value?.firstName ?? '',
          lastName: value?.lastName ?? '',
        }),
        value,
      })),
    [mandataries],
  );

  const inputs: IInput[] = [
    {
      type: 'text',
      label: 'Prénom',
      value: business.firstName,
      key: 'firstName',
      required: true,
      helperText: 'Veuillez entrer le prénom du client',
    },
    {
      type: 'text',
      label: 'Nom',
      value: business.lastName,
      key: 'lastName',
      required: true,
      helperText: 'Veuillez entrer le nom du client',
    },
    {
      type: 'email',
      label: 'Email',
      value: business.email,
      key: 'email',
      required: true,
      helperText: 'Veuillez entrer une adresse email valide',
    },
    {
      type: 'tel',
      label: 'Téléphone',
      value: business.phone,
      key: 'phone',
      required: true,
      helperText: 'Veuillez entrer un numéro de téléphone valide',
    },
    {
      type: 'autoComplete',
      label: 'Apporteur',
      value: business?.apaId,
      options: apaOptions,
      loading: apasLoading,
      key: 'apaId',
      required: true,
      helperText: 'Veuillez sélectionner un apporteur',
    },
    {
      type: 'autoComplete',
      label: 'Expert Connectcrédit',
      value: business?.mandataryId,
      options: mandataryOptions,
      loading: mandatariesLoading,
      key: 'mandataryId',
      required: true,
      helperText: 'Veuillez sélectionner un expert',
    },
  ];

  const handleChange = (key: string, value: unknown) => {
    setBusiness((prev: CreateBusinessInput) => ({ ...prev, [key]: value }));
  };

  const handleCreate = () => {
    setIsFinishFillInputs(true);
    if (isValidInputs()) {
      (async () => {
        const result = await createBusiness({
          variables: {
            input: business,
          },
        });

        if (result?.data?.createBusiness) {
          displaySnackBar(client, {
            type: 'SUCCESS',
            message: business?.isFormal
              ? `Une demande de consentement a été envoyée au client pour le transfert de ses coordonnées.`
              : `Le contact du client vient d'être transmis à l'Expert Connectcrédit de votre choix.`,
            isOpen: true,
          });
          onCreate();
        }
      })();
    }
  };

  const isValidInputs = () =>
    !!inputs?.every(({ type, value, required }) =>
      isValidInput(type, value, required),
    );

  return (
    <Box>
      {inputs.map((input) => {
        return (
          <Box key={input.key} m={1.5}>
            {(input.type === 'text' ||
              input.type === 'email' ||
              input.type === 'number') && (
              <CustomTextField
                type={input.type}
                label={input.label}
                value={input.value}
                required={input.required}
                error={
                  isFinishFillInputs &&
                  !isValidInput(input?.type, input?.value, input?.required)
                }
                helperText={
                  (isFinishFillInputs &&
                    !isValidInput(input?.type, input?.value, input?.required) &&
                    input?.helperText) ||
                  ''
                }
                onChange={(event) =>
                  handleChange(input.key, event.target.value)
                }
              />
            )}
            {input?.type === 'tel' && (
              <CustomPhoneInput
                label={input?.label}
                required={input?.required}
                value={input?.value}
                onChange={(value: any) => {
                  handleChange(input?.key, value as string);
                }}
                error={
                  isFinishFillInputs &&
                  !isValidInput(input?.type, input?.value, input?.required)
                }
                helperText={
                  (isFinishFillInputs &&
                    !isValidInput(input?.type, input?.value, input?.required) &&
                    input?.helperText) ||
                  ''
                }
              />
            )}
            {input?.type === 'autoComplete' && (
              <CustomAutoComplete
                onAutoCompleteChange={(_) =>
                  setBusiness((prev) => ({
                    ...prev,
                    [input?.key]: _?.value?.id,
                  }))
                }
                onChange={() => {}}
                options={input?.options ?? []}
                label={input?.label}
                loading={!!input?.loading}
                required={!!input?.required}
                error={isFinishFillInputs && input?.required && !input?.value}
                helperText={
                  (isFinishFillInputs &&
                    input?.required &&
                    !input?.value &&
                    input.helperText) ||
                  ''
                }
              />
            )}
          </Box>
        );
      })}
      <ConfirmationButtons
        handleCancel={onCancel}
        handleConfirm={handleCreate}
        disableConfirm={createBusinessLoading}
        confirmLoading={createBusinessLoading}
      />
    </Box>
  );
};

export default CreateBusinessForms;
